'use client';

import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useDeferredValue } from 'react';

import { pickupPointLabel, type DeliveryOption, type PickupPoint } from '@/entities/delivery';
import { citiesQueryOptions, deliveryOptionsQueryOptions } from '@/entities/delivery/client';
import type { DeliveryMethodId } from '@/entities/order';
import { rub } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { ChoiceCard } from '@/shared/ui/choice-card';
import { Icon, type IconName } from '@/shared/ui/icon';
import { TextButton } from '@/shared/ui/text-link';
import { TextField } from '@/shared/ui/text-field';

import styles from './delivery-step.module.scss';

export type DeliveryDraft = {
  city: string;
  method: DeliveryMethodId;
  point: PickupPoint | null;
  address: string;
  comment: string;
};

const ICONS: Record<DeliveryMethodId, IconName> = {
  pickup: 'pin',
  courier: 'courier',
  post: 'mail',
};

// Shown until tariffs for the typed city arrive (the cards stay usable while they load).
const placeholderMethods = (ready: boolean): DeliveryOption[] => [
  {
    id: 'pickup',
    title: 'Пункт выдачи или постамат',
    description: '[тариф] · [срок]',
    price: rub(0),
    isFree: false,
    available: ready,
  },
  {
    id: 'courier',
    title: 'Курьер',
    description: '[тариф] · [срок]',
    price: rub(0),
    isFree: false,
    available: ready,
  },
  {
    id: 'post',
    title: 'Почта России',
    description: '[тариф] · [срок]',
    price: rub(0),
    isFree: false,
    available: ready,
  },
];

export type DeliveryStepProps = {
  draft: DeliveryDraft;
  onChange: (draft: DeliveryDraft) => void;
  onOpenMap: () => void;
  onSubmit: () => void;
  tip: string;
  pointMeta: (point: PickupPoint) => string;
};

/** Step 02 "Получение" (05B): city, method, pickup point or address, comment. */
export function DeliveryStep({
  draft,
  onChange,
  onOpenMap,
  onSubmit,
  tip,
  pointMeta,
}: DeliveryStepProps) {
  const city = useDeferredValue(draft.city);
  const options = useQuery({
    ...deliveryOptionsQueryOptions(city),
    enabled: city.trim().length >= 2,
  });
  const cities = useQuery({ ...citiesQueryOptions(city), enabled: city.trim().length >= 1 });
  const methods: DeliveryOption[] = options.data ?? [];
  const set = (patch: Partial<DeliveryDraft>) => onChange({ ...draft, ...patch });
  const cityReady = city.trim().length >= 2;
  // Choosing the pickup card always opens the map (05B pickMethod), also when it is already selected.
  const pick = (id: DeliveryMethodId) => {
    set({ method: id });
    if (id === 'pickup') onOpenMap();
  };

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>Получение</h2>
      <TextField
        id="co-city"
        label="Город или населённый пункт"
        autoComplete="address-level2"
        list="co-cities"
        value={draft.city}
        onChange={(event) => set({ city: event.target.value, point: null })}
      />
      <datalist id="co-cities">
        {(cities.data ?? []).map((item) => (
          <option key={item.id} value={item.name}>
            {item.region ?? ''}
          </option>
        ))}
      </datalist>
      <div
        className={styles.methods}
        role="radiogroup"
        aria-label="Способ получения"
        aria-busy={options.isFetching || undefined}
      >
        {(methods.length ? methods : placeholderMethods(cityReady)).map((option) => (
          <ChoiceCard
            key={option.id}
            name="delivery-method"
            value={option.id}
            checked={draft.method === option.id}
            onSelect={() => pick(option.id)}
            onClick={option.id === 'pickup' && draft.method === 'pickup' ? onOpenMap : undefined}
            disabled={!option.available}
            title={option.title}
            icon={<Icon name={ICONS[option.id]} size={26} strokeWidth={1.3} />}
            description={
              option.available
                ? option.description
                : methods.length
                  ? 'Недоступно для этого города'
                  : 'Укажите город'
            }
          />
        ))}
      </div>
      {draft.method === 'pickup' &&
        (draft.point ? (
          <div className={styles.point}>
            <div className={styles['point-info']}>
              <Icon name="pin" size={20} strokeWidth={1.4} className={styles.pin} />
              <div className={styles['point-text']}>
                <span className={styles.accent}>Пункт выдачи выбран</span>
                <span className={styles['point-label']}>{pickupPointLabel(draft.point)}</span>
                <span className={styles.caption}>{pointMeta(draft.point)}</span>
              </div>
            </div>
            <TextButton onClick={onOpenMap}>Изменить</TextButton>
          </div>
        ) : (
          <div className={styles.point}>
            <span className={styles.muted}>Пункт выдачи не выбран</span>
            <Button variant="ghost" onClick={onOpenMap}>
              Выбрать пункт на карте
            </Button>
          </div>
        ))}
      {draft.method !== 'pickup' && (
        <TextField
          id="co-addr"
          label={draft.method === 'courier' ? 'Адрес доставки' : 'Адрес и индекс'}
          autoComplete="street-address"
          placeholder={
            draft.method === 'courier' ? 'Улица, дом, квартира' : 'Улица, дом, квартира, индекс'
          }
          value={draft.address}
          onChange={(event) => set({ address: event.target.value })}
        />
      )}
      <TextField
        id="co-comment"
        label="Комментарий к заказу · необязательно"
        placeholder="Например, удобное время звонка"
        value={draft.comment}
        maxLength={500}
        onChange={(event) => set({ comment: event.target.value })}
      />
      <div className={styles.submit}>
        <Button size="lg" onClick={onSubmit}>
          Перейти к оплате <Icon name="arrow-long-right" size={18} strokeWidth={1.3} />
        </Button>
        <output className={styles.tip}>{tip}</output>
        <p className={styles.caption}>
          Нажимая «Перейти к оплате», вы соглашаетесь с{' '}
          <Link className={styles.link} href={'/info/offer'}>
            условиями оферты
          </Link>{' '}
          и{' '}
          <Link className={styles.link} href={'/info/privacy'}>
            политикой обработки персональных данных
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
